import React from 'react';
import { motion } from 'framer-motion';
import { Award, Quote } from 'lucide-react';

export default function InhaberSection() {
  return (
    <section id="inhaber" data-testid="inhaber-section" className="py-24 sm:py-32 bg-[#F4F8F4] relative overflow-hidden">
      {/* Dezente Deko */}
      <div className="absolute top-0 left-0 w-80 h-80 bg-[#2D5A27]/5 rounded-full -translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#10B981]/5 rounded-full translate-x-1/3 translate-y-1/3" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12 sm:mb-16"
        >
          <span className="text-[#10B981] font-['Manrope'] text-sm font-semibold tracking-[0.2em] uppercase">
            Inhaber & Geschäftsführer
          </span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-[#1A3A22] tracking-tight mt-3 mb-4">
            Ihr persönlicher <span className="text-[#2D5A27]">Ansprechpartner</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Bild groß */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative">
              <img
                src="/inhaber-bild.jpeg"
                alt="Frank Daniels – Inhaber und Geschäftsführer der K.H. und B. Daniels Elektroinstallationen GmbH"
                className="w-full h-[420px] sm:h-[520px] object-cover border-4 border-white shadow-2xl"
                data-testid="inhaber-image"
              />
              {/* Dezenter grüner Akzent-Balken */}
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#2D5A27] -z-10 hidden sm:block" />
            </div>
          </motion.div>

          {/* Text rechts */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 bg-[#2D5A27] flex items-center justify-center">
                <Award className="w-6 h-6 text-[#10B981]" strokeWidth={1.5} />
              </div>
              <span className="font-['Manrope'] text-[#2D5A27] font-semibold text-sm tracking-wider uppercase">
                Elektromeister & Geschäftsführer
              </span>
            </div>

            <h3 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-[#1A3A22] mb-6 leading-tight">
              Frank Daniels
            </h3>

            <p className="font-['Manrope'] text-gray-700 text-base sm:text-lg leading-relaxed mb-6">
              Als Inhaber und Geschäftsführer der K.H. und B. Daniels Elektroinstallationen GmbH
              steht Frank Daniels persönlich für jedes Projekt ein. Mit langjähriger Erfahrung
              als Elektromeister verbindet er handwerkliche Präzision mit moderner Technik –
              und bleibt dabei Ihr direkter Ansprechpartner von der ersten Beratung bis zur Abnahme.
            </p>

            <div className="bg-white border-l-4 border-[#10B981] p-5 sm:p-6 mb-8" data-testid="inhaber-quote">
              <Quote className="w-7 h-7 text-[#10B981] mb-3" strokeWidth={1.5} />
              <p className="font-['Outfit'] text-[#1A3A22] italic text-base sm:text-lg leading-relaxed">
                &ldquo;Ich stehe persönlich für jede Installation, die wir abgeben.
                Beratung, Planung und Umsetzung – sauber, ehrlich und nach DIN-VDE-Normen.&rdquo;
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white border border-gray-100 p-4 text-center sm:text-left">
                <span className="font-['Outfit'] text-2xl font-bold text-[#2D5A27] block">Beratung</span>
                <span className="font-['Manrope'] text-gray-500 text-xs uppercase tracking-wider">Persönlich vor Ort</span>
              </div>
              <div className="bg-white border border-gray-100 p-4 text-center sm:text-left">
                <span className="font-['Outfit'] text-2xl font-bold text-[#2D5A27] block">Planung</span>
                <span className="font-['Manrope'] text-gray-500 text-xs uppercase tracking-wider">Auf Ihr Projekt zugeschnitten</span>
              </div>
              <div className="bg-white border border-gray-100 p-4 text-center sm:text-left">
                <span className="font-['Outfit'] text-2xl font-bold text-[#2D5A27] block">Umsetzung</span>
                <span className="font-['Manrope'] text-gray-500 text-xs uppercase tracking-wider">Fachgerecht & sauber</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
